// Copyright (c) Microgreen Manager LLC. All rights reserved. Proprietary and confidential. Unauthorized copying is strictly prohibited.
import { useLazyQuery, useMutation } from '@apollo/client';
import { clientLogger } from '@seedplanner/logger';
import { type WeightUnit, utils } from '@seedplanner/shared-utils';
import gql from 'graphql-tag';
import { enqueueSnackbar } from 'notistack';
import * as React from 'react';
import { useUserContext } from '../../context/UserContext';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Tooltip,
  Typography,
} from '../../lib/base';
import { AddCircleOutlineIcon } from '../../lib/icons';
import type {
  CropSizesDataQuery,
  CropSizesDataQueryVariables,
  EditCropSizesMutation,
  EditCropSizesMutationVariables,
} from '../../types/__generated__/graphql';
import { getNumericID } from '../../utils';
import { ExitDialogButton } from '../common/button';
import { ErrorView } from '../common/error';
import { PageLoading } from '../common/loading';
import NewSizeForm, { type SizeFormInput, type Props as NewSizeFormProps } from '../size/NewSizeForm';
import { formatCropDialogTitle } from './formatCropDialogTitle';

const EDIT_CROP_SIZES_MUTATION = gql`
  mutation EditCropSizes(
    $crop_id: Int!
    $sizes: [CreateProductSizeInput!]!
  ) {
    editCropSizes(
      input: {
        crop_id: $crop_id
        sizes: $sizes
      }
    ) {
      updated_crop {
        id
        updated_at
        sizes {
          size
          unit
        }
      }
    }
  }
`;

const CROP_SIZES_DATA_QUERY = gql`
  query CropSizesData($id: ID!) {
    crop(id: $id) {
      id
      sizes {
        size
        unit
      }
    }
  }
`;

interface Props {
  readonly cropId: string;
  readonly cropName: string;
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly onSuccess: () => void;
}

export default function EditCropSizesDialog({ isOpen, onClose, onSuccess, cropId, cropName }: Props) {
  const [fetchCrop, { data, error: dataError, loading: cropDataLoading }] = useLazyQuery<
    CropSizesDataQuery,
    CropSizesDataQueryVariables
  >(CROP_SIZES_DATA_QUERY, {
    fetchPolicy: 'network-only',
  });
  const [editCropSizes, { loading: editCropLoading }] = useMutation<
    EditCropSizesMutation,
    EditCropSizesMutationVariables
  >(EDIT_CROP_SIZES_MUTATION);
  const { preferredWeightUnit } = useUserContext();

  React.useEffect(() => {
    if (isOpen) {
      void fetchCrop({
        variables: { id: cropId },
        onCompleted: (cropData) => {
          const crop = cropData?.crop;
          if (crop != null) {
            const sizeForms = crop.sizes.map((size) => ({
              size: size.size.toString(),
              unit: size.unit as WeightUnit,
              sizeValidation: '',
            }));
            setSizeForms(
              sizeForms.map((values, idx) => {
                const formKey = utils.getRandomInt();
                return (
                  <NewSizeForm
                    key={formKey}
                    formKey={formKey}
                    values={values}
                    index={idx}
                    disableFields={disableFields}
                    onChangeField={handleSizeFormInputChange}
                    onRemove={removeSizeForm}
                  />
                );
              }),
            );
          }
        },
        onError: (errorIn) => {
          enqueueSnackbar(errorIn.message, { variant: 'error', autoHideDuration: 10000 });
        },
      });
    }
  }, [isOpen, cropId, fetchCrop]);

  const disableFields = cropDataLoading || editCropLoading;

  const [sizeForms, setSizeForms] = React.useState<React.ReactElement<NewSizeFormProps>[]>([]);

  const addSizeForm = () => {
    setSizeForms((prevForms) => {
      const formKey = utils.getRandomInt();
      return [
        ...prevForms,
        <NewSizeForm
          index={prevForms.length}
          key={formKey}
          formKey={formKey}
          disableFields={disableFields}
          values={{
            size: '',
            unit: preferredWeightUnit,
            sizeValidation: '',
          }}
          onChangeField={handleSizeFormInputChange}
          onRemove={removeSizeForm}
          // onEnter={onEnter}
        />,
      ];
    });
  };
  const removeSizeForm = (index: number) => {
    setSizeForms((prevForms) => {
      const updatedForms = [...prevForms];
      updatedForms.splice(index, 1);
      return updatedForms.map((old, idx) => React.cloneElement(old, { index: idx }));
    });
  };
  const handleSizeFormInputChange = (
    index: number,
    fieldName: keyof SizeFormInput,
    value: string | number | null,
    validationFieldName?: 'sizeValidation' | 'unit',
  ) => {
    setSizeForms((prevForms) => {
      const updatedForms = [...prevForms];
      let newValues = { ...updatedForms[index].props.values, [fieldName]: value };
      if (validationFieldName !== undefined && value !== '') {
        newValues = { ...newValues, [fieldName]: value };
      }
      updatedForms[index] = React.cloneElement(updatedForms[index], {
        values: newValues,
      });

      return updatedForms;
    });
  };

  const onSubmit = (_e?: React.MouseEvent | React.TouchEvent) => {
    void editCropSizes({
      variables: {
        sizes: sizeForms.map((form) => ({
          size: Number.parseFloat(form.props.values.size),
          unit: form.props.values.unit,
        })),
        crop_id: getNumericID(cropId),
      },
      onCompleted: (dataIn) => {
        if (dataIn !== undefined && dataIn.editCropSizes != null) {
          enqueueSnackbar('Updated crop sizes', { variant: 'success' });
          onSuccess();
        } else {
          enqueueSnackbar('Error editing crop, no data returned', { variant: 'error', autoHideDuration: 10000 });
        }
        setSizeForms([]);
      },
      onError: (errorIn) => {
        enqueueSnackbar(errorIn.message, { variant: 'error', autoHideDuration: 10000 });
        clientLogger.error({ title: 'edit_crop_error', message: errorIn.message });
      },
    });
  };

  const hasInputValidationError: boolean = sizeForms.some((form) => form.props.values.sizeValidation !== '');
  const requiredFieldsFilled: boolean = sizeForms.every((form) => form.props.values.size !== '');
  const isSubmitDisabled: boolean = !requiredFieldsFilled || hasInputValidationError || disableFields;

  const onCancelKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      onClose();
    }
  };
  const onSubmitKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      if (!isSubmitDisabled) {
        onSubmit();
      }
    }
  };

  const form = (
    <Box display="flex" flexDirection="column" sx={{ m: 1 }} gap={2}>
      {sizeForms.map((sizeForm) => (
        <Box key={sizeForm.props.formKey}>{sizeForm}</Box>
      ))}
      <Grid container display="flex" flexDirection="row" alignItems="center">
        <Tooltip
          title="Add a container size to your crop. This makes it convenient to create orders by container size later."
          placement="bottom-end"
        >
          <Button
            variant="outlined"
            arial-label="add-crop-container-size"
            onClick={addSizeForm}
            disabled={editCropLoading}
            startIcon={<AddCircleOutlineIcon />}
          >
            Add Container Size
          </Button>
        </Tooltip>
        <Grid flexGrow="1" />
      </Grid>
    </Box>
  );

  return (
    <Dialog
      open={isOpen}
      onClose={() => {
        onClose();
      }}
      maxWidth="md"
    >
      <DialogTitle>
        <Box display="flex" flexDirection="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">{formatCropDialogTitle('Edit Crop Container Sizes', cropName)}</Typography>
          <ExitDialogButton dontShiftUp onCloseDialog={onClose} />
        </Box>
      </DialogTitle>
      <DialogContent sx={{ padding: 1 }}>
        {dataError ? <ErrorView error={dataError} /> : cropDataLoading || data?.crop == null ? <PageLoading /> : form}
      </DialogContent>
      <DialogActions disableSpacing sx={{ flexDirection: { md: 'row', xs: 'column' }, alignItems: 'center' }}>
        <Button
          sx={{ width: { xs: '100%', md: '50%' } }}
          variant="outlined"
          onClick={onClose}
          onKeyDown={onCancelKeyDown}
        >
          Cancel
        </Button>
        <Button
          sx={{ width: { xs: '100%', md: '50%' }, ml: { md: 1 }, mt: { xs: 1, md: 0 } }}
          variant="contained"
          onClick={onSubmit}
          type="submit"
          disabled={isSubmitDisabled}
          onKeyDown={onSubmitKeyDown}
        >
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
}
